import { apiService } from '../api/api-client';
import type {
  Scope,
  SavedViewListResponse,
  SavedViewDetail,
  CreateSavedViewRequest,
  UpdateSavedViewRequest,
} from '@/features/saved-views/types';

/**
 * Build a scope-specific saved-views HTTP service.
 *
 * URLs follow the pattern `/api/v1/saved-views/<scope>/` — a dedicated
 * top-level dispatcher on the backend fans out to per-scope viewsets.
 */
export function createSavedViewsService<P = unknown>(scope: Scope) {
  const base = `/saved-views/${scope}`;

  return {
    scope,

    getList(): Promise<SavedViewListResponse> {
      return apiService.get<SavedViewListResponse>(`${base}/`);
    },

    getDetail(id: number): Promise<SavedViewDetail<P>> {
      return apiService.get<SavedViewDetail<P>>(`${base}/${id}/`);
    },

    create(data: CreateSavedViewRequest<P>): Promise<SavedViewDetail<P>> {
      return apiService.post<SavedViewDetail<P>>(`${base}/`, data);
    },

    update(id: number, data: UpdateSavedViewRequest<P>): Promise<SavedViewDetail<P>> {
      return apiService.patch<SavedViewDetail<P>>(`${base}/${id}/`, data);
    },

    remove(id: number): Promise<void> {
      return apiService.delete(`${base}/${id}/`);
    },

    setDefault(viewId: number | null): Promise<{ default_view_id: number | null }> {
      return apiService.put<{ default_view_id: number | null }>(
        `${base}/set-default/`,
        { saved_view_id: viewId },
      );
    },
  };
}

export type SavedViewsService<P = unknown> = ReturnType<typeof createSavedViewsService<P>>;
